/**
 * items.js — Firestore sync for the Daily Restock & Prep list.
 *
 * One flat collection, one doc per noted item:
 *   dailyRestockItems/{autoId}
 *     { date:'YYYY-MM-DD' (noted), listFor:'YYYY-MM-DD' (the list-day it's for),
 *       category, name, qty, status, done, doneAt, addedBy, addedAt,
 *       archived, archivedOn, archivedBy, archivedAt, carriedFrom? }
 * The Today board is the OPEN list (not archived); "Move list to History" archives it.
 *
 * Why per-item docs (not one doc per day): several staff note things at closing at
 * the same time, and independent docs merge cleanly with no last-write-wins clobber.
 * A single date-range listener feeds Today, History AND the learned suggestions, so
 * every device stays live. The `date` range query is single-field → no composite
 * index to deploy, and Firestore's offline cache serves it when the wifi drops.
 */
import {
  collection, doc, query, where, orderBy, limit,
  onSnapshot, getDocs, getDocsFromServer, setDoc, updateDoc, deleteDoc, serverTimestamp, writeBatch,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { daysAgoISO, localDateISO } from '../logic/dates';
import { isOpenItem } from '../logic/openList';
import { shareFeed } from './liveFeed';
import { rowTracker } from './rows';
import { debugLog, snapInfo } from './debug';
import { ITEMS_COLLECTION, RECENT_QUERY_LIMIT, HISTORY_FETCH_LIMIT, LIVE_WINDOW_DAYS, SETTLED_REFRESH_MS, SETTLED_SYNC_KEY } from '../config/constants';
import { STATUS, FLAG_STATUSES } from '../config/lists';

/** Items noted on/after `from` (and before `before`, when given), newest first. */
function itemsQuery(from, before, cap) {
  const range = before ? [where('date', '>=', from), where('date', '<', before)] : [where('date', '>=', from)];
  return query(collection(db, ITEMS_COLLECTION), ...range, orderBy('date', 'desc'), limit(cap));
}

/**
 * Live-subscribe to every item from the last `daysBack` days (newest first).
 * Calls onData(items[], { truncated }) — `truncated`: a query hit its cap, so the
 * oldest day loaded may be partial. Returns an unsubscribe function.
 *
 * Shared + lingering (liveFeed.js): the ticket-board drawer remounts Daily Ops on
 * every open, and a fresh listener after a quiet spell re-read the whole window.
 *
 * ── Only the last LIVE_WINDOW_DAYS listen to the server ─────────────────────────
 * Firestore bills a listener that reconnects after ~30 min away as a new query —
 * every doc in it again — and kitchen tablets and iPads sleep and wake all day. The
 * 60-day window had reached its 1,500-doc cap, so each wake of each device re-read
 * 1,500 docs; on 2026-10-22 that, with the feeds lingering beside it, used up the
 * 50k free-tier reads by mid-afternoon and the kitchen boards stopped updating.
 *
 * Only recent days really change. So the server listener covers LIVE_WINDOW_DAYS
 * (~200 docs a wake), and the older, settled days come from a cache-only listener:
 * no server reads, and it still shows this device's own edits. A server fetch
 * refreshes those days into the cache once SETTLED_REFRESH_MS has passed (see
 * config/constants.js for why no item can go missing). An item still on the open list from
 * before the live window pulls the window back to it, so the board is always live.
 */
export function subscribeRecentItems(daysBack, onData, onError) {
  const from = daysAgoISO(daysBack);
  if (daysBack <= LIVE_WINDOW_DAYS) {
    const key = `${ITEMS_COLLECTION}:since:${from}`;
    return shareFeed(key, (emit, fail) => {
      const rowsOf = rowTracker();
      return onSnapshot(itemsQuery(from, null, RECENT_QUERY_LIMIT), snap => {
        debugLog('snapshot', key, snapInfo(snap));
        emit(rowsOf(snap), { truncated: snap.size >= RECENT_QUERY_LIMIT });
      }, fail);
    }, onData, onError);
  }
  const liveFrom = daysAgoISO(LIVE_WINDOW_DAYS);
  return shareFeed(`${ITEMS_COLLECTION}:since:${from}:live:${liveFrom}`,
    (emit, fail) => openSplitFeed(from, liveFrom, emit, fail), onData, onError);
}

/**
 * The long window as two listeners: live from the server for [liveFrom, ∞), cache-only
 * for the settled days [from, liveFrom). Emits once both have answered, then on every
 * change of either. Returns stop().
 */
function openSplitFeed(from, liveFrom, emit, fail) {
  let live = null;            // server-backed rows, covering [liveStart, ∞)
  let liveStart = liveFrom;
  let liveCapped = false;
  let asked = liveFrom;       // the window the live listener was last opened with
  let stopLive = null;
  let settled = null;         // cached rows for [from, liveFrom)
  let settledCapped = false;
  let closed = false;

  const publish = () => {
    if (closed || !live || !settled) return;
    // Inside the live window the server is the truth — a cached copy there is stale.
    const older = settled.filter(i => i.date < liveStart);
    emit(live.concat(older), { truncated: liveCapped || settledCapped });
    // The open list must stay live: reach back to the oldest item still on it.
    const oldestOpen = older.reduce((m, i) => (isOpenItem(i) && (!m || i.date < m) ? i.date : m), '');
    if (oldestOpen && oldestOpen < asked) listenLive(oldestOpen);
  };

  const close = () => {
    closed = true;
    if (stopLive) stopLive();
    stopSettled();
  };

  // (Re)open the live listener from `start`. The old one keeps its rows on screen
  // until the wider window's first snapshot replaces them.
  function listenLive(start) {
    asked = start;
    const prev = stopLive;
    const rowsOf = rowTracker();
    stopLive = onSnapshot(itemsQuery(start, null, RECENT_QUERY_LIMIT), snap => {
      if (closed) return;
      debugLog('snapshot', `${ITEMS_COLLECTION}:live:${start}`, snapInfo(snap));
      live = rowsOf(snap);
      liveStart = start;
      liveCapped = snap.size >= RECENT_QUERY_LIMIT;
      publish();
    }, err => {
      if (closed) return;
      close();
      fail(err);
    });
    if (prev) prev();
  }

  const settledRows = rowTracker();
  const stopSettled = onSnapshot(itemsQuery(from, liveFrom, HISTORY_FETCH_LIMIT), { source: 'cache' }, snap => {
    if (closed) return;
    debugLog('snapshot', `${ITEMS_COLLECTION}:settled:${from}..${liveFrom}`, snapInfo(snap));
    const first = settled === null;
    settled = settledRows(snap);
    settledCapped = snap.size >= HISTORY_FETCH_LIMIT;
    if (first) refreshSettled(from, liveFrom, settled.length);
    publish();
  }, () => {
    // The local cache is unavailable: show the live days, fetch the rest once.
    if (closed || settled) return;
    settled = [];
    refreshSettled(from, liveFrom, 0);
    publish();
  });
  listenLive(liveFrom);
  return close;
}

/**
 * Re-read the settled days [from, before) from the server into the local cache — the
 * cache-only listener then shows them — unless this device did so within
 * SETTLED_REFRESH_MS. A cache that came back empty although the last refresh found
 * items has been cleared, so that refreshes at once.
 */
function refreshSettled(from, before, cachedCount) {
  let last = null;
  try { last = JSON.parse(localStorage.getItem(SETTLED_SYNC_KEY) || 'null'); } catch { /* unreadable → refresh */ }
  const age = last ? Date.now() - Number(last.at) : NaN;
  const recent = age >= 0 && age < SETTLED_REFRESH_MS;
  if (recent && !(cachedCount === 0 && last.n > 0)) return;
  getDocsFromServer(itemsQuery(from, before, HISTORY_FETCH_LIMIT))
    .then(snap => {
      debugLog('refresh', `${ITEMS_COLLECTION}:settled:${from}..${before}`, `${snap.size} docs (server)`);
      try { localStorage.setItem(SETTLED_SYNC_KEY, JSON.stringify({ at: Date.now(), n: snap.size })); } catch { /* storage blocked: refresh again next time */ }
    })
    .catch(() => { /* offline or over quota: the next mount tries again */ });
}

/**
 * One-shot read of items noted in [from, to] — History ranges older than the live
 * window. A plain get (not a listener) so browsing old history costs one read per
 * item once, not on every change. Single-field range → no composite index.
 */
export async function fetchItemsInRange(from, to) {
  const q = query(
    collection(db, ITEMS_COLLECTION),
    where('date', '>=', from), where('date', '<=', to),
    orderBy('date', 'desc'), limit(HISTORY_FETCH_LIMIT),
  );
  const snap = await getDocs(q);
  debugLog('fetch', `${ITEMS_COLLECTION}:range:${from}..${to}`, `${snap.size} docs`);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// A new board item's fields; `listFor` = the list-day it joins.
const newItem = ({ date, category, name, qty = '', addedBy = '', urgent = false, listFor }) => ({
  date, category, listFor: listFor || date,
  name: String(name || '').trim(),
  qty: String(qty || '').trim(),
  urgent: !!urgent,
  done: false, doneAt: null,
  addedBy,
  addedAt: serverTimestamp(),
});

// New docs get their id on the device, so an undo can name them at once — a write's
// promise only settles when the server answers, which offline (or with writes queued
// over the daily quota) can be hours later.

/**
 * Add one noted item for a given day/category.
 * @returns {{ id: string, saved: Promise }} — its id now, and the write
 */
export function addItem(fields) {
  const ref = doc(collection(db, ITEMS_COLLECTION));
  return { id: ref.id, saved: setDoc(ref, newItem(fields)) };
}

/**
 * Add several items in ONE write ("✨ Add my usual"): one round trip, and one security-
 * rules check instead of one per item (each rules check reads users/{uid}, a billed read).
 * @returns {{ ids: string[], saved: Promise }}
 */
export function addItems(list) {
  const batch = writeBatch(db);
  const ids = (list || []).map(fields => {
    const ref = doc(collection(db, ITEMS_COLLECTION));
    batch.set(ref, newItem(fields));
    return ref.id;
  });
  return { ids, saved: batch.commit() };
}

// Build the field patch for a status change. `done` stays in sync so existing
// history/queries keep working; who+when is stamped for whichever side applies.
function statusPatch(status, by) {
  const refilled = status === STATUS.REFILLED;
  const flagged  = FLAG_STATUSES.includes(status);
  return {
    status,
    done:      refilled,
    doneBy:    refilled ? by : '',
    doneAt:    refilled ? serverTimestamp() : null,
    flaggedBy: flagged ? by : '',
    flaggedAt: flagged ? serverTimestamp() : null,
  };
}

/** Set an item's refill status: 'needed' | 'refilled' | 'not_found' | 'oos'. */
export function setItemStatus(id, status, by = '') {
  return updateDoc(doc(db, ITEMS_COLLECTION, id), statusPatch(status, by));
}

/** Non-sourceable lists (cook/clean/issues) toggle done → refilled/needed. */
export function setItemDone(id, done, by = '') {
  return setItemStatus(id, done ? STATUS.REFILLED : STATUS.NEEDED, by);
}

/** Mark many items at once (Review-tab multi-select / "refill all"). */
export function bulkSetStatus(ids, status, by = '') {
  if (!ids || ids.length === 0) return Promise.resolve();
  const batch = writeBatch(db);
  const patch = statusPatch(status, by);
  ids.forEach(id => batch.update(doc(db, ITEMS_COLLECTION, id), patch));
  return batch.commit();
}

/** Restore each item to a specific prior status — powers Undo on status/bulk changes. */
export function restoreStatuses(entries, by = '') {
  const list = (entries || []).filter(e => e && e.id && e.status);
  if (!list.length) return Promise.resolve();
  const batch = writeBatch(db);
  list.forEach(({ id, status }) => batch.update(doc(db, ITEMS_COLLECTION, id), statusPatch(status, by)));
  return batch.commit();
}

/**
 * Move items off the active board into History (they stay in the collection, still
 * dated, so History keeps them) — or bring them back. Powers the Today panels'
 * "Move to history →" and its undo. Also frees the name to be noted again (a busy
 * weekend might need the same item twice).
 */
export function setItemsArchived(ids, archived, by = '') {
  if (!ids || ids.length === 0) return Promise.resolve();
  const batch = writeBatch(db);
  ids.forEach(id => batch.update(doc(db, ITEMS_COLLECTION, id), archivePatch(archived, by)));
  return batch.commit();
}

// Who/when stamps for History's "Closed" column (cleared again on undo).
export function archivePatch(archived, by) {
  return archived
    ? { archived: true, archivedOn: localDateISO(), archivedBy: by || '', archivedAt: serverTimestamp() }
    : { archived: false, archivedOn: null, archivedBy: null, archivedAt: null };
}

/**
 * "Move list to History": archive `archiveIds` and carry `carryIds` (unfinished items
 * the user chose to keep) onto the new list-day `toDay`, remembering where they came
 * from. One atomic batch. Resolve the returned undo spec with `reopenList`.
 */
export function closeList({ archiveIds = [], carry = [], toDay, by = '' }) {
  const batch = writeBatch(db);
  archiveIds.forEach(id => batch.update(doc(db, ITEMS_COLLECTION, id), archivePatch(true, by)));
  carry.forEach(({ id, fromDay }) => batch.update(doc(db, ITEMS_COLLECTION, id), { listFor: toDay, carriedFrom: fromDay || null }));
  return batch.commit();
}
/** Undo for closeList: un-archive, and put carried items back on their old list-day. */
export function reopenList({ archiveIds = [], carry = [] }) {
  const batch = writeBatch(db);
  archiveIds.forEach(id => batch.update(doc(db, ITEMS_COLLECTION, id), archivePatch(false)));
  carry.forEach(({ id, listFor, carriedFrom }) => batch.update(doc(db, ITEMS_COLLECTION, id), { listFor: listFor || null, carriedFrom: carriedFrom || null }));
  return batch.commit();
}

/** Patch an item's fields in place (name, urgent, category, …). */
export function updateItem(id, patch) {
  return updateDoc(doc(db, ITEMS_COLLECTION, id), patch);
}

/** Remove an item from the list. */
export function removeItem(id) {
  return deleteDoc(doc(db, ITEMS_COLLECTION, id));
}
/** Undo of removeItem: the item back exactly as it was — same id, status, who and when. */
export function restoreItem(row) {
  const { id, ...data } = row;
  return setDoc(doc(db, ITEMS_COLLECTION, id), data);
}
